import React, {FC, ReactNode, useRef} from 'react';
import styled, {css} from 'styled-components';

import {buildInteractionIdProps} from '../../../../libs/shared/core/src/helpers/interaction/interactionHelpers';
import {useLocalize} from '../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {defineMessage} from '../../../../libs/shared/core/src/helpers/internationalization/message';
import {usePrevious} from '../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {AsyncStatusesEnum} from '../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {UnreachableError} from '../../../../libs/shared/core/src/helpers/types/customErrors';
import {
  DataEventTrackingProvider,
  wrapInDataEventContext,
} from '../../../../libs/shared/core/src/services/tracking/dataEventTrackingProvider';
import {CellsPanelId, ViewerPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {RepositionBoundary} from '../../../../libs/shared/ui/src/components/reposition/repositionBoundary';
import {usePushRoom} from '../../../../libs/shared/ui/src/hooks/push/usePushRoom';
import {LegacyInteractionParent} from '../../../../libs/shared/ui/src/interactionTracking/interactionParent';
import {HandoffActivityTypesEnum} from '../../../../srcCommon/app/handoff/handoffModel';
import {useAppDispatch} from '../../../helpers/appReduxHelpers';
import {areParametersCellsEqual} from '../../../store/panels/inboxes/parameters/inboxesParametersHelpers';
import {navigateViewerAsyncAction} from '../../../store/panels/viewer/viewerPanelActions';
import {computeViewerPanelPagePushRoomName} from '../../../store/panels/viewer/viewerPanelHelpers';
import {ViewerPanelPageState, ViewerPanelPageTypesEnum} from '../../../store/panels/viewer/viewerPanelState';
import {UserActivity} from '../../app/userActivity/userActivity';
import {ErrorReporterContainer} from '../../errors/errorReporterContainer';
import {P3InfoBubble} from '../../infoBubbles/p3InfoBubble';
import {ConversationHighlights} from './conversation/highlights/conversationHighlights';
import {viewerMinWidth} from './conversation/viewerConstants';
import {EmptyViewerContent} from './empty/emptyViewerContent';
import {MultiConversationsViewerContentContainer} from './multiConversations/multiConversationsViewerContentContainer';
import {SingleConversationViewerContent} from './singleConversation/singleConversationViewerContent';
import {ViewerLoader} from './viewerLoader';

/*
 * Props.
 */

export interface ViewerProps {
  panelId: ViewerPanelId;
  cellsPanelId: CellsPanelId | undefined;
  renderTopbar?: (props: ViewerTopbarsProps) => ReactNode;
  renderHeader?: (props: ViewerTopbarsProps) => ReactNode;
  contentPage: ViewerPanelPageState;
  shouldFocusComment?: boolean;
  canComposerPopout: boolean;
  isInPopup: boolean;
  /** Whether this component is visible on screen. Used for Apple Handoff. */
  isActive: boolean;
}

export interface ViewerTopbarsProps {
  isOffline: boolean;
}

/*
 * Style.
 */

interface ViewerStyleProps {
  $isInPopup: boolean;
}

// minmax(1px, 1fr) is a workaround for https://frontjira.atlassian.net/browse/PB-4864 which is
// caused by https://bugs.chromium.org/p/chromium/issues/detail?id=833837 and affects the Electron app.
const StyledViewerDiv = styled.div<ViewerStyleProps>`
  display: grid;
  grid-template-columns: 100%;
  grid-template-rows: auto auto minmax(1px, 1fr);
  grid-template-areas:
    'topbar'
    'header'
    'timeline-and-comment';

  min-width: ${viewerMinWidth}px;
  min-height: 250px;
  background-color: ${(p) => p.theme.appBackground};

  ${(p) =>
    p.$isInPopup &&
    css`
      padding-left: 10px;
      padding-right: 2px;
    `};
`;

const StyledTopbarsWrapper = styled.div`
  grid-area: topbar / header;
`;

/*
 * Component.
 */

export const Viewer: FC<ViewerProps> = (props) => {
  const localize = useLocalize();
  const viewerRef = useRef<HTMLDivElement | null>(null);

  const {parameters} = props.contentPage;
  const {isActive} = props;
  const previousParameters = usePrevious(parameters);

  // Whenever we see new cells parameters, remember whether we were initially empty.
  const wasEmptyInitiallyRef = useRef(false);
  if (!previousParameters || !areParametersCellsEqual(previousParameters, parameters)) {
    wasEmptyInitiallyRef.current = props.contentPage.type === ViewerPanelPageTypesEnum.NO_CONVERSATION;
  }

  const dispatch = useAppDispatch();
  const onRetryClick = () => {
    dispatch(navigateViewerAsyncAction(props.panelId, props.contentPage.parameters));
  };

  const roomName = computeViewerPanelPagePushRoomName(props.contentPage);
  const {isReceiving} = usePushRoom(roomName);
  const topbarsProps: ViewerTopbarsProps = {
    isOffline: !isReceiving,
  };

  return (
    <ErrorReporterContainer errorKey={props.contentPage.uid} isTopLevel={false}>
      <DataEventTrackingProvider
        context={{
          name: 'p3',
          payload: {conversation_id: findDataEventConversationId(props.contentPage), section: 'timeline'},
        }}
      >
        <LegacyInteractionParent interactionId="viewer">
          <UserActivity type={HandoffActivityTypesEnum.VIEWER} userInfo={{parameters}} isActive={isActive} />
          <StyledViewerDiv
            ref={viewerRef}
            role="region"
            aria-label={localize(
              defineMessage({
                id: 'viewer-region',
                message: 'Viewer',
              }),
            )}
            data-searchparent
            $isInPopup={props.isInPopup}
          >
            <RepositionBoundary boundaryRef={viewerRef}>
              {renderContent(props, topbarsProps, wasEmptyInitiallyRef.current, onRetryClick)}
            </RepositionBoundary>
          </StyledViewerDiv>
          {!props.isInPopup && <P3InfoBubble anchor={viewerRef} />}
        </LegacyInteractionParent>
      </DataEventTrackingProvider>
    </ErrorReporterContainer>
  );
};

function renderContent(
  props: ViewerProps,
  topbarsProps: ViewerTopbarsProps,
  wasEmptyInitially: boolean,
  onRetryClick: () => void,
) {
  const {renderTopbar = () => null, renderHeader = () => null, contentPage} = props;
  const wrapperProps = buildInteractionIdProps('viewer-topbar');
  const wrapHeader = (header: ReactNode) => wrapInDataEventContext(header, 'p3', {section: 'header'});

  if (contentPage.status !== AsyncStatusesEnum.LOADED) {
    return (
      <ViewerLoader
        topbar={renderTopbar(topbarsProps)}
        header={wrapHeader(renderHeader(topbarsProps))}
        status={contentPage.status}
        onRetryClick={onRetryClick}
      />
    );
  }

  switch (contentPage.type) {
    case ViewerPanelPageTypesEnum.SINGLE_CONVERSATION: {
      return (
        <ConversationHighlights
          panelId={props.panelId}
          globalQuery={contentPage.cellsContext.searchQuery}
          page={contentPage}
        >
          <StyledTopbarsWrapper {...wrapperProps}>
            {renderTopbar(topbarsProps)}
            {wrapHeader(renderHeader(topbarsProps))}
          </StyledTopbarsWrapper>
          <SingleConversationViewerContent
            panelId={props.panelId}
            page={contentPage}
            shouldFocusComment={props.shouldFocusComment ?? false}
            canComposerPopout={props.canComposerPopout}
          />
        </ConversationHighlights>
      );
    }

    case ViewerPanelPageTypesEnum.MULTI_CONVERSATIONS:
      return (
        <>
          <StyledTopbarsWrapper {...wrapperProps}>
            {renderTopbar(topbarsProps)}
            {wrapHeader(renderHeader(topbarsProps))}
          </StyledTopbarsWrapper>
          <MultiConversationsViewerContentContainer
            panelId={props.panelId}
            cellsPanelId={props.cellsPanelId}
            cellsContext={contentPage.cellsContext}
            inboxesParameters={contentPage.parameters}
          />
        </>
      );

    case ViewerPanelPageTypesEnum.NO_CONVERSATION:
    default:
      return (
        <EmptyViewerContent
          cellsContext={contentPage.cellsContext}
          inboxesParameters={contentPage.parameters}
          didBecomeEmpty={!wasEmptyInitially}
        />
      );
  }
}

function findDataEventConversationId(page: ViewerPanelPageState) {
  const {type} = page;
  switch (type) {
    case ViewerPanelPageTypesEnum.NO_CONVERSATION:
    case ViewerPanelPageTypesEnum.MULTI_CONVERSATIONS:
      return undefined;
    case ViewerPanelPageTypesEnum.SINGLE_CONVERSATION:
      return page.conversationId;
    default:
      throw new UnreachableError(type);
  }
}
